import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import { useChat } from "src/hooks/useChat";
import { disconnectSocket } from "src/services/socketChatService.jsx";

const ChatContext = createContext(null);

export const ChatProvider = ({ children }) => {
  const {
    isConnected,
    joinChat,
    send,
    fetchMessages,
    leaveChat,
    autoJoinChats,
    listenNewMessage,
    listenMessages,
    listenMessagesRead,
    getMessages,
    getUnreadCount,
    disconnect,
  } = useChat();

  // Get user info from Redux store
  const user = useSelector((state) => state?.user?.userinfo);
  const privileges = useSelector((state) => state?.user?.privileges);

  // Check if user is an admin (Operations, Manager, Admin, Dispatcher)
  const isAdmin = useCallback(() => {
    if (!user) return false;

    // Check user role or permissions
    const userRole = user.role?.name?.toLowerCase() || "";
    const adminRoles = ["operations", "manager", "admin", "dispatcher"];

    return adminRoles.some((role) => userRole.includes(role));
  }, [user]);

  // Auto-join manifest chats when admin user connects
  useEffect(() => {
    if (isConnected && isAdmin()) {
      // TODO: Fetch open manifest chats from API and auto-join them
      // This should be implemented based on your API structure
      // Example:
      // fetchOpenManifestChats().then(manifests => {
      //   const manifestUuids = manifests.map(m => m.uuid);
      //   autoJoinChats(manifestUuids);
      // });

      console.log("Admin user connected, ready to auto-join manifest chats");
    }
  }, [isConnected, isAdmin, autoJoinChats]);

  // Set up event listeners for new messages
  useEffect(() => {
    const cleanupNewMessage = listenNewMessage((data) => {
      console.log("New message received:", data);
      // Handle new message - update UI, show notification, etc.
    });

    return () => {
      if (cleanupNewMessage) cleanupNewMessage();
    };
  }, [listenNewMessage]);

  // Set up event listeners for messages list
  useEffect(() => {
    const cleanupMessages = listenMessages((data) => {
      console.log("Messages list received:", data);
      // Handle messages list - update UI
    });

    return () => {
      if (cleanupMessages) cleanupMessages();
    };
  }, [listenMessages]);

  // Set up event listeners for read receipts
  useEffect(() => {
    const cleanupMessagesRead = listenMessagesRead((data) => {
      console.log("Messages read event received:", data);
      // Handle read receipts - update UI to show read status
    });

    return () => {
      if (cleanupMessagesRead) cleanupMessagesRead();
    };
  }, [listenMessagesRead]);

  // Disconnect socket on logout
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      disconnect();
    };
  }, [disconnect]);

  const value = {
    // Connection status
    isConnected,

    // Chat operations
    joinChat,
    send,
    fetchMessages,
    leaveChat,
    autoJoinChats,

    // Data accessors
    getMessages,
    getUnreadCount,

    // User info
    user,
    isAdmin: isAdmin(),

    // Cleanup
    disconnect,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
};

export default ChatContext;
